import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface Company {
  id: string;
  nome_empresa: string;
  razao_social?: string;
  cnpj?: string;
  logo_url?: string;
  cor_primaria?: string;
  cor_secundaria?: string;
  moeda: string;
  endereco_rua?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  telefone_comercial?: string;
  email_comercial?: string;
  whatsapp_business?: string;
  site?: string;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  status: string;
  plano?: string;
}

interface CompanyContextType {
  // Estado
  company: Company | null;
  loading: boolean;
  userRole: 'owner' | 'admin' | 'user' | null;
  
  // Ações
  createCompany: (companyData: Partial<Company>) => Promise<{ data?: Company; error?: any }>;
  updateCompany: (companyId: string, updates: Partial<Company>) => Promise<{ error?: any }>;
  refreshCompany: () => Promise<void>;
  
  // Utilitários - RBAC completo
  hasPermission: (permission: 'read' | 'write' | 'admin') => boolean;
  canManageUsers: () => boolean;
  isOwner: () => boolean;
  isAdmin: () => boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'user' | null>(null);

  // Buscar role do usuário
  const fetchUserRole = async (userId: string, companyId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_user_role', {
        _user_id: userId,
        _empresa_id: companyId
      });
      
      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }
      
      return data as 'owner' | 'admin' | 'user' | null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  // Carregar empresa do usuário
  const loadUserCompany = async () => {
    if (!user || !user.id) {
      setCompany(null);
      setUserRole(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Sempre via RPC: resolve tanto dono quanto membro da empresa
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_company', {
        user_id: user.id
      });

      if (rpcError) {
        throw rpcError;
      }

      // Mapear dados da empresa do RPC
      if (rpcData && rpcData.length > 0) {
        const companyData = rpcData[0];
        
        const userCompany: Company = {
          id: companyData.empresa_id,
          nome_empresa: companyData.nome_empresa,
          razao_social: companyData.razao_social,
          cnpj: companyData.cnpj,
          logo_url: companyData.logo_url,
          cor_primaria: companyData.cor_primaria,
          cor_secundaria: companyData.cor_secundaria,
          moeda: companyData.moeda,
          endereco_rua: companyData.endereco_rua,
          endereco_numero: companyData.endereco_numero,
          endereco_complemento: companyData.endereco_complemento,
          endereco_bairro: companyData.endereco_bairro,
          endereco_cidade: companyData.endereco_cidade,
          endereco_estado: companyData.endereco_estado,
          endereco_cep: companyData.endereco_cep,
          telefone_comercial: companyData.telefone_comercial,
          email_comercial: companyData.email_comercial,
          whatsapp_business: companyData.whatsapp_business,
          site: companyData.site,
          instagram: companyData.instagram,
          facebook: companyData.facebook,
          linkedin: companyData.linkedin,
          status: companyData.status,
          plano: companyData.plano
        };

        setCompany(userCompany);
        
        // Buscar role do usuário
        const role = await fetchUserRole(user.id, companyData.empresa_id);
        setUserRole(role);
      } else {
        // Sem empresa é um estado esperado enquanto o onboarding está pendente
        setCompany(null);
        setUserRole(null);
      }
    } catch (error) {
      const errorMessage = error?.message || 'Erro desconhecido ao carregar empresa';
      toast.error(`Erro ao carregar empresa: ${errorMessage}`);
      setCompany(null);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUserCompanyRef = useRef(loadUserCompany);
  loadUserCompanyRef.current = loadUserCompany;

  // Criar nova empresa
  const createCompany = async (companyData: Partial<Company>) => {
    try {
      const { data, error } = await supabase.rpc('create_company_simple', {
        p_nome_empresa: companyData.nome_empresa || 'Nova Empresa',
        p_razao_social: companyData.razao_social || null,
        p_cnpj: companyData.cnpj || null,
        p_logo_url: companyData.logo_url || null,
        p_moeda: companyData.moeda || 'BRL'
      });

      if (error) {
        throw error;
      }

      // Recarregar empresa
      await loadUserCompany();
      
      toast.success('Empresa criada com sucesso!');
      return { data: data as unknown as Company };
    } catch (error) {
      const errorMessage = error?.message || 'Erro desconhecido ao criar empresa';
      toast.error(`Erro ao criar empresa: ${errorMessage}`);
      return { error };
    }
  };

  // Atualizar empresa
  const updateCompany = async (companyId: string, updates: Partial<Company>) => {
    try {
      const { error } = await supabase
        .from('empresas')
        .update(updates)
        .eq('id', companyId);

      if (error) throw error;

      // Atualizar estado local
      setCompany(prev => prev ? { ...prev, ...updates } : null);

      toast.success('Empresa atualizada com sucesso!');
      return {};
    } catch (error) {
      toast.error('Erro ao atualizar empresa');
      return { error };
    }
  };

  // Verificar permissões com RBAC
  const hasPermission = (permission: 'read' | 'write' | 'admin'): boolean => {
    if (!company || !userRole) return false;
    
    if (permission === 'read') return true; // Todos podem ler
    if (permission === 'write') return userRole !== 'user'; // Admin+ pode escrever
    if (permission === 'admin') return userRole === 'owner' || userRole === 'admin';
    
    return false;
  };

  const canManageUsers = (): boolean => {
    return userRole === 'owner' || userRole === 'admin';
  };

  const isOwner = (): boolean => {
    return userRole === 'owner';
  };

  const isAdmin = (): boolean => {
    return userRole === 'admin' || userRole === 'owner';
  };

  // Carregar empresa quando o usuário muda
  useEffect(() => {
    loadUserCompany();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escutar evento de refresh da empresa
  useEffect(() => {
    const handleRefresh = () => {
      loadUserCompanyRef.current();
    };

    window.addEventListener('refreshCompany', handleRefresh);
    return () => {
      window.removeEventListener('refreshCompany', handleRefresh);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Método público para refresh
  const refreshCompany = async () => {
    await loadUserCompany();
  };

  const value = {
    company,
    loading,
    userRole,
    createCompany,
    updateCompany,
    refreshCompany,
    hasPermission,
    canManageUsers,
    isOwner,
    isAdmin,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}